import api from '../../../api/client'
import { authAPI } from '../../../api'

/**
 * @typedef {Object} SupportTicketFilters
 * @property {number} [page]
 * @property {number} [pageSize]
 * @property {string} [status]
 * @property {string} [priority]
 * @property {string} [category]
 * @property {string} [dateFrom]
 * @property {string} [dateTo]
 */

/**
 * @param {SupportTicketFilters} params
 */
export async function listSupportTickets(params = {}) {
  const {
    page = 1,
    pageSize = 10,
    status = '',
    priority = '',
    category = '',
    tenantId = '',
    assignedAgentId = '',
    dateFrom = '',
    dateTo = '',
    hasUnseenOnly = false,
  } = params

  const query = { page, pageSize }
  if (status) query.status = String(status).toUpperCase()
  if (priority) query.priority = String(priority).toUpperCase()
  if (category) query.category = String(category).toUpperCase()
  if (tenantId) query.tenantId = Number(tenantId)
  if (assignedAgentId) query.assignedAgentId = Number(assignedAgentId)
  if (dateFrom) query.dateFrom = dateFrom
  if (dateTo) query.dateTo = dateTo
  if (hasUnseenOnly) query.hasUnseenOnly = true

  const { data } = await api.get('/support-tickets', { params: query })
  return {
    rows: Array.isArray(data?.rows) ? data.rows : [],
    count: Number(data?.count || 0),
    limit: Number(data?.limit || pageSize),
    offset: Number(data?.offset || 0),
    totalCount: Number(data?.totalCount || 0),
  }
}

export async function listPlatformAgents() {
  const { data } = await api.get('/support-tickets/platform/agents')
  return Array.isArray(data) ? data : []
}

export async function listPlatformTenants() {
  const { data } = await api.get('/support-tickets/platform/tenants')
  return Array.isArray(data) ? data : []
}

export async function listTenantUsers(tenantId) {
  const { data } = await api.get('/platform-staff/tenant-users', {
    params: { tenant_id: Number(tenantId) },
  })
  return Array.isArray(data) ? data : []
}

export async function listPlatformStaff() {
  const { data } = await api.get('/platform-staff')
  return Array.isArray(data) ? data : []
}

export async function grantPlatformRole(payload) {
  const { data } = await api.post('/platform-staff', payload)
  return data
}

export async function revokePlatformRole(staffId) {
  const { data } = await api.delete(`/platform-staff/${Number(staffId)}`)
  return data
}

export async function markTicketAsRead(ticketId) {
  const { data } = await api.post(`/support-tickets/${Number(ticketId)}/mark-read`)
  return data
}

export async function assignTicketToAgent(ticketId, agentUserId) {
  const { data } = await api.patch(`/support-tickets/${Number(ticketId)}/assign`, {
    agentUserId: Number(agentUserId),
  })
  return data
}

export async function getUnreadIndicators() {
  const { data } = await api.get('/support-tickets/unread-indicators')
  return Array.isArray(data?.tickets) ? data.tickets : []
}

export async function getSupportTicket(id) {
  const { data } = await api.get(`/support-tickets/${Number(id)}`)
  return data
}

export async function createSupportTicket(payload) {
  const requestPayload = {
    subject: String(payload?.subject || '').trim(),
    description: String(payload?.description || '').trim(),
    category: String(payload?.category || 'ISSUE').toUpperCase(),
    priority: String(payload?.priority || 'MEDIUM').toUpperCase(),
  }
  const { data } = await api.post('/support-tickets', requestPayload)
  return data
}

export async function updateSupportTicket(id, payload) {
  const body = {}
  if (payload?.subject != null) body.subject = String(payload.subject).trim()
  if (payload?.description != null) body.description = String(payload.description).trim()
  if (payload?.category != null) body.category = String(payload.category).toUpperCase()
  if (payload?.priority != null) body.priority = String(payload.priority).toUpperCase()
  if (payload?.status != null) body.status = String(payload.status).toUpperCase()
  const { data } = await api.patch(`/support-tickets/${Number(id)}`, body)
  return data
}

export async function listTicketComments(ticketId) {
  const { data } = await api.get(`/support-tickets/${Number(ticketId)}/comments`)
  return Array.isArray(data) ? data : []
}

export async function createTicketComment(ticketId, comment, { reopenTicket = false } = {}) {
  const { data } = await api.post(`/support-tickets/${Number(ticketId)}/comments`, {
    comment: String(comment || '').trim(),
    reopenTicket,
  })
  return data
}

export async function uploadSupportTicketAttachment(ticketId, file) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post(`/support-tickets/${Number(ticketId)}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteSupportTicketAttachment(ticketId, attachmentId) {
  const { data } = await api.delete(`/support-tickets/${Number(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`)
  return data
}

export async function downloadSupportTicketAttachment(ticketId, attachmentId) {
  const response = await api.get(`/support-tickets/${Number(ticketId)}/attachments/${encodeURIComponent(attachmentId)}/download`, {
    responseType: 'blob',
  })
  return {
    blob: response.data,
    contentType: response.headers?.['content-type'] || 'application/octet-stream',
  }
}

export async function getCurrentUserProfile() {
  return authAPI.getProfile()
}
